// Internal trace-id capture state, shared by the public entry points of this package.
//
// This file intentionally is NOT named `index.ts` so that adding new exports here does not
// expand the package's public barrel file (see the "No expanding existing src/index barrel
// files" ratcheting rule). Public surface area is exposed explicitly via subpath exports in
// package.json (`.` and `./start-capturing-trace-ids-by-predicate`).

export type TraceCaptureContext = {
	operationName: string;
	variables: Record<string, unknown>;
};

export type TraceCapturePredicate = (context: TraceCaptureContext) => boolean;

export type TraceCapture = {
	getTraces: () => readonly string[];
	dispose: () => void;
};

type PredicateCapture = {
	predicate: TraceCapturePredicate;
	traces: string[];
};

const OperationTraceIdMap: { [operationName: string]: string[] | undefined } = {};

const predicateCaptures = new Set<PredicateCapture>();

export const startCapturingTraceIdsForOperation = (operationName: string): void => {
	OperationTraceIdMap[operationName] = OperationTraceIdMap[operationName] || [];
};

export const startCapturingTraceIdsForPredicate = (
	predicate: TraceCapturePredicate,
): TraceCapture => {
	const capture: PredicateCapture = { predicate, traces: [] };
	predicateCaptures.add(capture);
	return {
		getTraces: () => capture.traces.slice(),
		dispose: () => {
			predicateCaptures.delete(capture);
		},
	};
};

export const stopCapturingTraceIdsForOperation = (operationName: string): void => {
	OperationTraceIdMap[operationName] = undefined;
};

export const recordTraceId = (
	operationName: string,
	traceId?: string,
	variables?: Record<string, unknown>,
): void => {
	if (traceId === undefined) {
		return;
	}

	OperationTraceIdMap[operationName]?.push(traceId);

	if (predicateCaptures.size === 0) {
		return;
	}

	const context: TraceCaptureContext = {
		operationName,
		variables: variables ?? {},
	};
	for (const capture of [...predicateCaptures]) {
		if (capture.predicate(context)) {
			capture.traces.push(traceId);
		}
	}
};

export const getCapturedTraceIds = (operationName: string): string[] =>
	OperationTraceIdMap[operationName] || [];
